import { CSSProperties } from "react";
import {
  CardConditionEnum,
  CardLanguageEnum,
  FinishEnum,
} from "modules/cards/model/card-enums";
import {
  CardPriceCurrenciesEnum,
  CardPriceMarketEnum,
} from "modules/cards/model/card-price";
import { DeckPagedItem } from "modules/decks/model/deck";
import { PagedTradeListItemsResponse } from "modules/tradeLists/model/tradeList";
import { SetType } from "modules/cards/model/set";

// Generic product surfaces accept every syntactically valid game identifier
// returned by the capability manifest. Bundled game IDs are owned by
// gameRegistry.ts rather than repeated in a second enum here.
export type GameId = string;

export interface GameCapabilities {
  catalogue: boolean;
  trading: boolean;
  onboarding: boolean;
  deckBuilder: boolean;
  legalities: boolean;
  scanner: boolean;
}

export type GameCapability = keyof GameCapabilities;

export type GamePricingVariant = "cardmarket-eur" | "tcgplayer-usd";

export type FacetControl =
  | "game-pills"
  | "group"
  | "searchable-group"
  | "range-input"
  | "hybrid-range";

export type FacetFieldKind =
  | "string-array"
  | "integer-array"
  | "integer"
  | "float"
  | "match-mode";

export type FacetContext = "collection" | "cards" | "trade";
export type FacetPurpose = "game-scope" | "list-status";

export type FacetPresentation =
  | "plain"
  | "condition"
  | "mana-color"
  | "domain"
  | "card-type";

export interface FacetFilterField {
  key: string;
  queryKey: string;
  kind: FacetFieldKind;
}

export interface GameFacetDefinition {
  key: string;
  title: string;
  facetKey: string;
  purpose?: FacetPurpose;
  control: FacetControl;
  contexts?: readonly FacetContext[];
  selection?: FacetFilterField;
  range?: {
    min: FacetFilterField;
    max: FacetFilterField;
    step?: number;
    isCurrency?: boolean;
  };
  matchMode?: FacetFilterField;
  presentation?: FacetPresentation;
  exclusiveValue?: string | number;
  requiresMultipleGames?: boolean;
}

export interface GameDefinition {
  id: GameId;
  capabilities: GameCapabilities;
  pricingVariants: GamePricingVariant[];
  facets: GameFacetDefinition[];
  finishes: GameFinishDefinition[];
}

export interface GameManifest {
  games: GameDefinition[];
  sharedFacets: GameFacetDefinition[];
}

export type FinishPriceKey =
  | "nonfoil"
  | "foil"
  | "metal"
  | "metalDeluxe"
  | "etched";
export type FinishTimestampKey =
  | "nonfoilTimestamp"
  | "foilTimestamp"
  | "metalTimestamp"
  | "metalDeluxeTimestamp"
  | "etchedTimestamp";

export interface GameFinishDefinition {
  value: string;
  label: string;
  order: number;
  surface: boolean;
  foilVisual: boolean;
  priceKey?: FinishPriceKey;
  timestampKey?: FinishTimestampKey;
}

/**
 * Presentation metadata for bundled games. Games without bundled copy or
 * assets use getGameMetadata's neutral fallback; runtime exposure comes
 * exclusively from the server capability manifest.
 */
export interface GameMetadata {
  id: GameId;
  /** Full marketing name, e.g. "Magic: The Gathering". */
  name: string;
  /** Short marketing name used in headings and CTAs, e.g. "MTG". */
  shortName: string;
  /** One-liner for selector tiles and onboarding cards. */
  description: string;
  /** Brand color used as a default for the game's icon when one isn't passed in. */
  brandColor: string;
  /** Bundled-fallback visibility; the server manifest is authoritative at runtime. */
  hidden: boolean;
}

const GAME_ID_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

export function isGameId(value: unknown): value is GameId {
  return (
    typeof value === "string" &&
    value.length <= 64 &&
    GAME_ID_PATTERN.test(value)
  );
}

export interface ServerError {
  code: number;
  text: string;
  message: string;
  human?: boolean;
}

export interface LegalitiesPaletteColorOptions {
  legal?: string;
  banned: string;
  notLegal?: string;
  restricted: string;
}

export type ConditionsPaletteColorOptions = {
  [key in CardConditionEnum]: string;
};
export type ShortCardSet = {
  code: string;
  name: string;
  releaseDate: string;
  type: SetType;
  keyruneCode: string;
  game: string;
};
export type CardSets = Record<GameId, Record<string, ShortCardSet>>;

export interface ShortSet {
  name: string;
  code: string;
  releaseDate: string;
  keyruneCode: string;
  game: string;
}

export interface CardStyle {
  chart?: CSSProperties;
  card?: CSSProperties;
}

export enum CardGrouping {
  FirstType,
  ManaValue,
  Color,
  ColorIdentity,
  None,
}

export enum CardDisplay {
  Table,
  VisualStack,
  VisualSpoiler,
  VisualGrid,
  VisualSplit,
}

export type CardLanguageCountryCodes = {
  [key in CardLanguageEnum]: string;
};

export const languageCountryCodes: CardLanguageCountryCodes = {
  [CardLanguageEnum.English]: "GB",
  [CardLanguageEnum.AncientGreek]: "",
  [CardLanguageEnum.Arabic]: "SA",
  [CardLanguageEnum.ChineseSimplified]: "CN",
  [CardLanguageEnum.ChineseTraditional]: "TW",
  [CardLanguageEnum.French]: "FR",
  [CardLanguageEnum.German]: "DE",
  [CardLanguageEnum.Hebrew]: "IL",
  [CardLanguageEnum.Italian]: "IT",
  [CardLanguageEnum.Japanese]: "JP",
  [CardLanguageEnum.Korean]: "KR",
  [CardLanguageEnum.Latin]: "",
  [CardLanguageEnum.Phyrexian]: "",
  [CardLanguageEnum.PortugueseBrazil]: "BR",
  [CardLanguageEnum.Russian]: "RU",
  [CardLanguageEnum.Sanskrit]: "",
  [CardLanguageEnum.Spanish]: "ES",
};

export type CardConditionStrings = {
  [key in CardConditionEnum]: string;
};

export const cardConditionStrings: CardConditionStrings = {
  [CardConditionEnum._]: "",
  [CardConditionEnum.Poor]: "PO",
  [CardConditionEnum.Played]: "PL",
  [CardConditionEnum.LightPlayed]: "LP",
  [CardConditionEnum.Good]: "GD",
  [CardConditionEnum.Excellent]: "EX",
  [CardConditionEnum.NearMint]: "NM",
  [CardConditionEnum.Mint]: "M",
};

export const cardConditionStringsFull: CardConditionStrings = {
  [CardConditionEnum._]: "",
  [CardConditionEnum.Poor]: "Poor",
  [CardConditionEnum.Played]: "Played",
  [CardConditionEnum.LightPlayed]: "Lightly played",
  [CardConditionEnum.Good]: "Good",
  [CardConditionEnum.Excellent]: "Excellent",
  [CardConditionEnum.NearMint]: "Near mint",
  [CardConditionEnum.Mint]: "Mint",
};

export interface UserPreferences {
  games: GameId[];
  tradeLocations: TradeLocationPreference[];
  notifications: {
    tradeEnabled: boolean;
    chatEnabled: boolean;
    emailChatEnabled: boolean;
    emailDigestEnabled: boolean;
  };
  privacy: {
    showLGS: boolean;
    showActivityStatus: boolean;
  };
  pricing: PricingConfiguration;
}

/** PATCH /users/:id/preferences updates only the supplied preference fields. */
export interface UserPreferencesPatch {
  games?: UserPreferences["games"];
  tradeLocations?: UserPreferences["tradeLocations"];
  notifications?: Partial<UserPreferences["notifications"]>;
  privacy?: Partial<UserPreferences["privacy"]>;
  pricing?: UserPreferences["pricing"];
}

export interface TradeLocationPreference {
  id: string;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
}

export interface PricingConfiguration {
  market: CardPriceMarketEnum;
  currency: CardPriceCurrenciesEnum;
}

export interface ShortUser {
  id: string;
  username: string;
  email: string;
  preferredAvatar: string;
  lgs?: ShortLGS[];
  createdAt?: string;
  /** Flat fields from the store-roster endpoint (profile pages use preferences.games). */
  games?: string[];
  cardCount?: number;
  /** Profile-only fields, populated by the user Find endpoint. */
  completedTrades?: number;
  cosmetics?: UserCosmetics;
  activityStatus?: ActivityStatus;
  preferences: UserPreferences;
}

export const USER_BADGES = {
  REFERRAL_REWARD_01: "referral_reward_01",
  REFERRAL_REWARD_02: "referral_reward_02",
} as const;

export type UserBadge = (typeof USER_BADGES)[keyof typeof USER_BADGES];

export interface UserCosmetics {
  badges?: UserBadge[];
}

export type ActivityStatus =
  | "active"
  | "recently"
  | "some_time_ago"
  | "long_time_ago";

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  // Empty means no explicit game filter; capability-aware consumers expose
  // every game enabled for their surface.
  games: [],
  tradeLocations: [],
  notifications: {
    tradeEnabled: true,
    chatEnabled: true,
    emailChatEnabled: true,
    emailDigestEnabled: true,
  },
  privacy: {
    showLGS: true,
    showActivityStatus: true,
  },
  pricing: {
    market: CardPriceMarketEnum.Cardmarket,
    currency: CardPriceCurrenciesEnum.EUR,
  },
};

export interface ShortLGS {
  slug: string;
  wotcId: number;
  name: string;
  latitude: number;
  longitude: number;
  phoneNumber: string;
  emailAddress: string;
  showEmailInSEL: boolean;
  isTradingHub: boolean;
  userCount?: number;
}

export const flippableCardLayouts = [
  "double_faced_token",
  "flip",
  "meld",
  "modal_dfc",
  "reversible_card",
  "transform",
];

export interface UserProfilePageData {
  user: ShortUser;
  decks: DeckPagedItem[] | null;
  recentTrades: PagedTradeListItemsResponse | null;
  recentWants: PagedTradeListItemsResponse | null;
}
